import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Search, Filter, Phone, Loader2, RefreshCw, ChevronLeft, ChevronRight, ExternalLink, Zap, CheckSquare, Square, PhoneCall, PhoneMissed, User, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateRangePicker } from '../components/ui/date-range-picker';

function getScoreBadge(score) {
  if (score === null || score === undefined) return { cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30', label: 'N/A' };
  if (score >= 90) return { cls: 'score-excellent', label: 'Excellent' };
  if (score >= 75) return { cls: 'score-good', label: 'Good' };
  if (score >= 60) return { cls: 'score-review', label: 'Needs Review' };
  return { cls: 'score-critical', label: 'Critical' };
}

// Does the (already-parsed) Claude transcript hold any real content?
function hasTranscriptContent(transcript) {
  if (!transcript) return false;
  if (typeof transcript === 'string') return transcript.trim() !== '';
  if (typeof transcript === 'object') {
    return (Array.isArray(transcript.segments) && transcript.segments.length > 0)
      || String(transcript.full_text || '').trim() !== '';
  }
  return false;
}

// Derive the Gemini (auto-processing) status the same way the backend
// /processing/gemini/calls endpoint does, so the Calls list reflects the
// Gemma pipeline rather than the Claude/manual processing_status. Returns a
// human label + badge classes covering every scenario: completed / failed /
// archived / in-flight stages / pending / ineligible.
function getGeminiStatusInfo(call) {
  const status = call.gemini_status || null;

  if (status === 'failed') return { label: 'Failed', cls: 'bg-rose-500/15 text-rose-300' };
  if (status === 'completed') return { label: 'Completed', cls: 'bg-emerald-500/15 text-emerald-300' };
  if (status === 'archived') return { label: 'Archived', cls: 'bg-slate-500/15 text-slate-300' };

  const hasTranscript = hasTranscriptContent(call.transcript);
  const hasGeminiTranscript = String(call.gemini_transcript || '').trim() !== '';
  const hasGeminiAnalysis = call.gemini_analysis != null;

  if (status === 'processing') {
    if (!hasTranscript) return { label: 'Transcribing', cls: 'bg-sky-500/15 text-sky-300 status-pulse' };
    if (!hasGeminiTranscript) return { label: 'Translating', cls: 'bg-amber-500/15 text-amber-300 status-pulse' };
    return { label: 'Analyzing', cls: 'bg-teal-500/15 text-teal-300 status-pulse' };
  }

  // gemini_status IS NULL → not yet picked up by the pipeline. Surface every
  // such call as "New" so the badge matches the "New" filter option.
  if (hasGeminiAnalysis) return { label: 'Completed', cls: 'bg-emerald-500/15 text-emerald-300' };
  return { label: 'New', cls: 'bg-blue-500/15 text-blue-300' };
}

// Derive the Claude (manual/legacy) pipeline status from processing_status.
// Used for the Status column when a call was analyzed by Claude, so the badge
// reflects the Claude run instead of the Gemma pipeline. Mirrors the stage
// labels used in CallDetailPage's processing_status handling.
function getClaudeStatusInfo(call) {
  switch (call.processing_status) {
    case 'completed': return { label: 'Completed', cls: 'bg-emerald-500/15 text-emerald-300' };
    case 'failed': return { label: 'Failed', cls: 'bg-rose-500/15 text-rose-300' };
    case 'downloading': return { label: 'Downloading', cls: 'bg-sky-500/15 text-sky-300 status-pulse' };
    case 'transcribing': return { label: 'Transcribing', cls: 'bg-sky-500/15 text-sky-300 status-pulse' };
    case 'translating': return { label: 'Translating', cls: 'bg-amber-500/15 text-amber-300 status-pulse' };
    case 'analyzing': return { label: 'Analyzing', cls: 'bg-teal-500/15 text-teal-300 status-pulse' };
    case 'voice_analyzing': return { label: 'Analyzing', cls: 'bg-teal-500/15 text-teal-300 status-pulse' };
    default: return { label: 'New', cls: 'bg-blue-500/15 text-blue-300' };
  }
}

// A call counts as "analyzed by Claude" when call.analysis holds a real result
// (an overall_score). When true, the Status column shows the Claude status;
// otherwise it falls back to the Gemma pipeline status.
function isAnalyzedByClaude(call) {
  return Boolean(call.analysis && Object.keys(call.analysis).length > 0 && call.analysis.overall_score !== undefined);
}

const ACTIVE_STATUSES = new Set(['downloading', 'transcribing', 'translating', 'analyzing', 'voice_analyzing']);

export default function CallsListPage() {
  const navigate = useNavigate();
  const { isAdmin, isAgent, isSupervisor, user } = useAuth();
  const canProcess = isAdmin || isSupervisor;

  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '', direction: '', agent_name: '', from_date: '', to_date: '' });
  const [availableAgentNames, setAvailableAgentNames] = useState([]);
  const [showAgentSuggestions, setShowAgentSuggestions] = useState(false);
  // Default to time-based sort (most recent first) for all roles.
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [answeredTab, setAnsweredTab] = useState('answered_long'); // 'answered_long' | 'answered_short' | 'unanswered' | 'all'
  const [callTypeTab, setCallTypeTab] = useState('supervised'); // 'supervised' | 'my_calls' (for supervisors)

  // Selection state
  const [selected, setSelected] = useState(new Set());
  const [processingIds, setProcessingIds] = useState(new Set());
  const [analyzingIds, setAnalyzingIds] = useState(new Set());
  const [batchProcessing, setBatchProcessing] = useState(false);
  const latestRequestIdRef = useRef(0);

  const fetchCalls = useCallback(async () => {
    const requestId = ++latestRequestIdRef.current;
    setLoading(true);
    try {
      // The status filter targets the Gemini (auto-processing) pipeline, so
      // send it as `gemini_status` rather than the Claude `status` param.
      const { status: geminiStatusFilter, ...restFilters } = filters;
      const params = {
        page, limit: 20, search, sort_by: sortBy, sort_order: sortOrder, ...restFilters,
        ...(geminiStatusFilter ? { gemini_status: geminiStatusFilter } : {}),
        ...(answeredTab !== 'all' ? { answered_type: answeredTab } : {}),
      };

      // For supervisors, add call type filtering
      if (isSupervisor) {
        if (callTypeTab === 'my_calls') {
          // My Calls should only show the supervisor's own extension.
          // If unavailable, force an empty result set instead of falling back to supervised agents.
          params.agent_number = user?.agent_number || '__no_agent_number__';
          // My Calls is answered-only by requirement.
          params.answered_type = 'answered';
        }
        // For 'supervised', the backend will automatically filter to supervised agents
      }

      const res = await api.getCalls(params);
      if (requestId !== latestRequestIdRef.current) {
        return;
      }
      setCalls(res.calls || []);
      setTotal(res.total || 0);
      setPages(res.pages || 1);
    } catch (err) {
      if (requestId === latestRequestIdRef.current) {
        toast.error('Failed to load calls');
      }
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [page, search, filters, sortBy, sortOrder, answeredTab, callTypeTab, isSupervisor, user?.agent_number]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  useEffect(() => {
    api.getAgentsList()
      .then((res) => {
        const names = Array.isArray(res?.agents)
          ? res.agents
            .map((a) => (typeof a === 'object' ? (a.agent_name || a.agent_number || '') : String(a)))
            .filter(Boolean)
          : [];
        setAvailableAgentNames(names);
      })
      .catch(() => { });
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  // --------- Selection helpers ---------
  // const toggleSelect = (callId) => {
  //   const call = calls.find((entry) => entry.call_id === callId);
  //   if (Number(call?.answered_seconds ?? 0) <= 30) {
  //     return;
  //   }

  //   setSelected(prev => {
  //     const next = new Set(prev);
  //     if (next.has(callId)) next.delete(callId); else next.add(callId);
  //     return next;
  //   });
  // };

  // const toggleSelectAll = () => {
  //   const eligibleCallIds = calls.filter((call) => Number(call?.answered_seconds ?? 0) > 30).map((call) => call.call_id);
  //   if (eligibleCallIds.length === 0) {
  //     return;
  //   }

  //   const allEligibleSelected = eligibleCallIds.every((callId) => selected.has(callId));

  //   if (allEligibleSelected) {
  //     setSelected(new Set());
  //   } else {
  //     setSelected(new Set(eligibleCallIds));
  //   }
  // };

  // --------- Manual processing ---------
  // const handleProcessSingle = async (callId, e) => {
  //   e.stopPropagation();
  //   setProcessingIds(prev => new Set(prev).add(callId));
  //   try {
  //     await api.processCall(callId);
  //     toast.success('Processing started');
  //     setTimeout(fetchCalls, 3000);
  //   } catch (err) {
  //     const msg = err.message || 'Failed';
  //     toast.error(msg.includes('already being processed') ? 'Already processing' : msg);
  //   } finally {
  //     setProcessingIds(prev => { const n = new Set(prev); n.delete(callId); return n; });
  //   }
  // };

  // const handleAnalyzeSingle = async (callId, e) => {
  //   e.stopPropagation();
  //   setAnalyzingIds(prev => new Set(prev).add(callId));
  //   try {
  //     await api.analyzeCall(callId);
  //     toast.success('Analysis started');
  //     setTimeout(fetchCalls, 3000);
  //   } catch (err) {
  //     const msg = err.message || 'Failed';
  //     toast.error(msg.includes('already being processed') ? 'Already processing' : msg);
  //   } finally {
  //     setAnalyzingIds(prev => { const n = new Set(prev); n.delete(callId); return n; });
  //   }
  // };

  // const handleProcessSelected = async () => {
  //   if (selected.size === 0) { toast.error('No calls selected'); return; }
  //   setBatchProcessing(true);
  //   try {
  //     const res = await api.processSelected(Array.from(selected));
  //     toast.success(res.message);
  //     setSelected(new Set());
  //     setTimeout(fetchCalls, 3000);
  //   } catch (err) {
  //     toast.error(err.message || 'Batch processing failed');
  //   } finally {
  //     setBatchProcessing(false);
  //   }
  // };

  const openCallDetailsInNewTab = (callId, e) => {
    e.stopPropagation();
    const detailPath = `/calls/${encodeURIComponent(callId)}`;

    // Open first to avoid popup blockers, then hydrate auth state in the new tab.
    const newTab = window.open('', '_blank');
    if (!newTab) {
      navigate(detailPath);
      return;
    }

    try {
      const token = sessionStorage.getItem('vcm_token');
      const userData = sessionStorage.getItem('vcm_user');
      if (token) {
        newTab.sessionStorage.setItem('vcm_token', token);
      }
      if (userData) {
        newTab.sessionStorage.setItem('vcm_user', userData);
      }
      newTab.location.href = detailPath;
    } catch (error) {
      newTab.location.href = detailPath;
    }
  };


  const clearFilters = () => {
    setSearch('');
    setFilters({ status: '', direction: '', agent_name: '', from_date: '', to_date: '' });
    setSortBy('created_at');
    setSortOrder('desc');
    setPage(1);
  };

  const agentNameQuery = filters.agent_name.trim().toLowerCase();
  const agentNameSuggestions = availableAgentNames
    .filter((name) => {
      if (!agentNameQuery) return true;
      return String(name).toLowerCase().includes(agentNameQuery);
    })
    .slice(0, 8);

  const handleAgentNameSelect = (name) => {
    setFilters((prev) => ({ ...prev, agent_name: name }));
    setPage(1);
    setShowAgentSuggestions(false);
  };

  const eligibleCallIds = calls.filter((call) => Number(call?.answered_seconds ?? 0) >= 30).map((call) => call.call_id);
  const allEligibleSelected = eligibleCallIds.length > 0 && eligibleCallIds.every((callId) => selected.has(callId));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Call Records</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} total records</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Show "Process Selected" when items are selected */}
          {false && canProcess && selected.size > 0 && (
            <Button size="sm" onClick={handleProcessSelected} disabled={batchProcessing} data-testid="calls-process-selected-btn">
              {batchProcessing
                ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                : <Zap className="w-4 h-4 mr-1" />}
              Process {selected.size} Selected
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={fetchCalls}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {[
          { key: 'answered_long', label: 'Answered (>30s)', icon: PhoneCall },
          { key: 'answered_short', label: 'Answered (≤30s)', icon: PhoneCall },
          { key: 'unanswered', label: 'Unanswered', icon: PhoneMissed },
          { key: 'all', label: 'All Calls', icon: Phone },
          ...(isSupervisor ? [{ key: 'my_calls', label: 'My Calls', icon: User }] : []),
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => {
              if (key === 'my_calls') {
                setCallTypeTab('my_calls');
                setAnsweredTab('answered'); // My calls must remain answered-only
              } else {
                setAnsweredTab(key);
                setCallTypeTab('supervised'); // Reset to supervised when using answered tabs
              }
              setPage(1);
            }}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${(key === 'my_calls'
              ? callTypeTab === 'my_calls'
              : callTypeTab !== 'my_calls' && answeredTab === key)
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by agent or caller..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-card"
            data-testid="calls-search-input"
          />
        </form>
        <Button variant="secondary" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="calls-filter-button">
          <Filter className="w-4 h-4 mr-1" />
          Filters
        </Button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="bg-card border border-border rounded-xl p-4 flex flex-wrap xl:flex-nowrap items-center gap-2">
          <select
            value={filters.status}
            onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
            className="flex-1 min-w-[120px] bg-background border border-border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Status</option>
            <option value="new">New</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
          <select
            value={filters.direction}
            onChange={(e) => { setFilters({ ...filters, direction: e.target.value }); setPage(1); }}
            className="flex-1 min-w-[120px] bg-background border border-border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Directions</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
          </select>
          <div className="relative flex-1 min-w-[140px]">
            <Input
              type="text"
              placeholder="Agent name"
              value={filters.agent_name}
              onFocus={() => setShowAgentSuggestions(true)}
              onBlur={() => setTimeout(() => setShowAgentSuggestions(false), 120)}
              onChange={(e) => {
                setFilters({ ...filters, agent_name: e.target.value });
                setPage(1);
                setShowAgentSuggestions(true);
              }}
              className="bg-background w-full"
            />
            {showAgentSuggestions && agentNameSuggestions.length > 0 && (
              <div className="absolute left-0 top-full z-30 mt-1 w-full max-h-52 overflow-auto rounded-lg border border-border bg-background">
                {agentNameSuggestions.map((name) => (
                  <button
                    key={name}
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-accent/60"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleAgentNameSelect(name);
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <DateRangePicker
            value={{ from_date: filters.from_date, to_date: filters.to_date }}
            showValue={!!(filters.from_date || filters.to_date)}
            placeholder="Select date range"
            onChange={(range) => {
              setFilters((prev) => ({ ...prev, from_date: range.from_date, to_date: range.to_date }));
              setPage(1);
            }}
            onClear={() => {
              setFilters((prev) => ({ ...prev, from_date: '', to_date: '' }));
              setPage(1);
            }}
            className="flex-[1.5] min-w-[180px]"
            buttonClassName="w-full min-w-0 h-9"
          />
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
            className="flex-1 min-w-[120px] bg-background border border-border rounded-lg px-3 py-2 text-sm"
          >
            {!isAgent && <option value="effective_score">Sort: Score</option>}
            <option value="created_at">Sort: Time</option>
            <option value="call_duration">Sort: Duration</option>
            <option value="agent_name">Sort: Agent</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}
            className="flex-1 min-w-[100px] bg-background border border-border rounded-lg px-3 py-2 text-sm"
          >
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </select>
          <Button variant="ghost" size="sm" onClick={clearFilters} className="shrink-0" data-testid="calls-clear-filters-btn">Clear</Button>
        </div>
      )}

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="calls-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                {/* Select-all checkbox */}
                {false && canProcess && (
                  <th className="px-3 py-3 w-10">
                    <button onClick={toggleSelectAll} className="text-muted-foreground hover:text-foreground" data-testid="calls-select-all">
                      {allEligibleSelected
                        ? <CheckSquare className="w-4 h-4 text-primary" />
                        : <Square className="w-4 h-4" />}
                    </button>
                  </th>
                )}
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Time</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Agent</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Caller</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Direction</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Duration</th>
                {!isAgent && <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Score</th>}
                {!isAgent && <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Model</th>}
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Status</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={canProcess ? 10 : 9} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></td></tr>
              ) : calls.length === 0 ? (
                <tr><td colSpan={canProcess ? 10 : 9} className="text-center py-12 text-sm text-muted-foreground">No calls found. Try fetching records from the Dashboard.</td></tr>
              ) : calls.map(call => {
                const claudeScore = call.analysis?.overall_score;
                const gemmaScore = call.gemini_analysis?.overall_score;
                // Single combined score: show the Claude score when the call was
                // analyzed by Claude, otherwise the Gemma score. The Model column
                // labels which source the displayed score came from.
                const analyzedByClaude = isAnalyzedByClaude(call);
                const effectiveScore = analyzedByClaude ? claudeScore : gemmaScore;
                const effectiveBadge = getScoreBadge(effectiveScore);
                const modelLabel = analyzedByClaude ? 'Claude' : 'Gemma';
                const isSelected = selected.has(call.call_id);
                const isProcessingNow = ACTIVE_STATUSES.has(call.processing_status) || processingIds.has(call.call_id) || analyzingIds.has(call.call_id);
                const isProcessable = Number(call?.answered_seconds ?? 0) >= 30;
                const hasTranscript = (call.transcript?.segments || []).length > 0;
                const hasAnalysis = call.analysis != null;
                const canProcessCall = canProcess && !isProcessingNow && call.recording_url && isProcessable && !hasTranscript && call.processing_status !== 'completed';
                const canAnalyzeCall = canProcess && !isProcessingNow && isProcessable && hasTranscript && !hasAnalysis;

                return (
                  <tr
                    key={call.call_id || call.id}
                    className={`border-b border-border/50 hover:bg-white/[0.02] transition-colors cursor-pointer ${isSelected ? 'bg-primary/[0.04]' : ''}`}
                    onClick={(e) => openCallDetailsInNewTab(call.call_id, e)}
                    data-testid="call-row">
                    {false && canProcess && (
                      <td className="px-3 py-3" onClick={(e) => { e.stopPropagation(); toggleSelect(call.call_id); }}>
                        {isSelected
                          ? <CheckSquare className="w-4 h-4 text-primary" />
                          : <Square className={`w-4 h-4 ${isProcessable ? 'text-muted-foreground hover:text-foreground' : 'text-muted-foreground/40'}`} />}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span className="text-sm">{call.date}</span>
                      <span className="text-xs text-muted-foreground ml-1">{call.time}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">{call.agent_name}</td>
                    <td className="px-4 py-3 text-sm font-mono text-muted-foreground">{call.client_number}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${call.direction === 'inbound' ? 'bg-sky-500/15 text-sky-300' : 'bg-teal-500/15 text-teal-300'}`}>
                        {call.direction}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm tabular-nums">{call.call_duration}s</td>
                    {!isAgent && (
                      <td className="px-4 py-3">
                        {effectiveScore !== null && effectiveScore !== undefined ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${effectiveBadge.cls}`} title={`${modelLabel}: ${effectiveBadge.label}`} data-testid="call-score-badge">
                            {effectiveScore} - {effectiveBadge.label}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </td>
                    )}
                    {!isAgent && (
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${analyzedByClaude ? 'bg-violet-500/15 text-violet-300' : 'bg-sky-500/15 text-sky-300'}`} data-testid="call-model-badge">
                          {modelLabel}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      {(() => {
                        // When Claude analyzed this call, surface the Claude
                        // status; otherwise fall back to the Gemma pipeline.
                        const status = isAnalyzedByClaude(call)
                          ? getClaudeStatusInfo(call)
                          : getGeminiStatusInfo(call);
                        return (
                          <span className={`text-xs px-2 py-0.5 rounded ${status.cls}`} data-testid="call-status-badge">
                            {status.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Process Call button — no transcript yet (hidden) */}
                        {false && canProcessCall && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-primary"
                            onClick={(e) => handleProcessSingle(call.call_id, e)}
                            disabled={processingIds.has(call.call_id)}
                            title="Process this call"
                            data-testid="call-process-btn"
                          >
                            {/* {processingIds.has(call.call_id)
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                              : <Zap className="w-3.5 h-3.5 mr-1" />} */}
                            Process
                          </Button>
                        )}
                        {/* Analyze button — transcript exists but no analysis (hidden) */}
                        {false && canAnalyzeCall && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-teal-400"
                            onClick={(e) => handleAnalyzeSingle(call.call_id, e)}
                            disabled={analyzingIds.has(call.call_id)}
                            title="Analyze this call"
                            data-testid="call-analyze-btn"
                          >
                            {/* {analyzingIds.has(call.call_id)
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                              : <Zap className="w-3.5 h-3.5 mr-1" />} */}
                            Analyze
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={(e) => openCallDetailsInNewTab(call.call_id, e)} data-testid="call-row-open-button">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border" data-testid="calls-pagination">
            <span className="text-xs text-muted-foreground">Page {page} of {pages} ({total} records)</span>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
